import { Link } from "react-router-dom";
import { useState } from "react";
import logo1 from "../assets/thrombiocolourlogo1.webp";
import aboutIcon from "../assets/aboutIcon.webp";
import aboutIconMobile from "../assets/aboutIconMobile.webp";
import publicationMediaIcon from "../assets/publicationsIcon.webp";
import publicationMediaIconMobile from "../assets/publicationsIconMobile.webp";
import leadershipIcon from "../assets/leadershipIcon.webp";
import trialsIcon from "../assets/clinicalTrials.webp"
import trialsIconMobile from "../assets/clinicalTrialsMobile.webp"
import leadershipIconMobile from "../assets/leadershipIconMobile.webp";
import olcdots from "../assets/olcdots.webp";
import technologyIcon from "../assets/circle31.webp";
import mobileCurveFooter from "../assets/curveimgnewnewnewmobile62.webp";
import HomeOverlay from "../components/HomeOverlay";
import FadeInSection from "../components/FadeInSection";
import LazyImage from "../components/LazyImage";
import SEO from "../components/SEO";
import ScrollDepthTracker from "../components/ScrollDepthTracker";
import { trackEvent } from "../utils/analytics";

// News popup (NBN News story) — disabled. Set to true to show it again.
const NEWS_OVERLAY_ENABLED = false;

export default function Home() {
  const [showOverlay, setShowOverlay] = useState(NEWS_OVERLAY_ENABLED);

  const handleCloseOverlay = () => {
    setShowOverlay(false);
  };

  // Schema data for the Home page
  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Thrombio",
    "url": "https://www.thrombio.com.au/",
    "logo": "https://www.thrombio.com.au/thromBioInvert.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "info@thrombio.com.au"
    },
    "sameAs": [
      "https://www.linkedin.com/company/thrombioholdings"
    ],
    "description": "Thrombio is a drug discovery company dedicated to advancing the global standard of care for the treatment of stroke using novel anti-thrombotic drugs with unprecedented safety and efficacy."
  };

  return (
    <>
      <SEO
        title="Revolutionizing Stroke Care"
        description="Building on 30 years of world-leading research in blood clotting and stroke, TBO-309 shows promising Phase II data, potentially revolutionizing stroke care worldwide."
        keywords={['Stroke Treatment', 'ThromBio', 'Drug Development', 'TBO-309', 'Biotechnology']}
        ogImage="/Thrombio.gif"
        schema={homeSchema}
      />
      <ScrollDepthTracker pageCategory="home" pageName="index" />

      <div className="bg-primary homeBg -mb-80 pb-80">
        <div className="relative md:pageContainer">
          <div className="flex max-md:flex-col max-md:gap-10 px-6 md:px-12 md:items-center justify-center pb-32 xl:pb-[40rem]">
            <div className="flex flex-col gap-4">
              <FadeInSection direction="left" alwaysAnimate>
                <img
                  id="heroLogo"
                  src={logo1}
                  alt="Thrombio logo - Innovators in Stroke Therapies"
                  width={439}
                  className="h-auto max-w-full"
                />
              </FadeInSection>
              <FadeInSection className="text-5xl md:max-xl:text-[2.4vw] md:max-xl:leading-normal md:max-xl:whitespace-nowrap tracking-wider pl-[15px] delay-1000" direction="left" alwaysAnimate>
                <h1 className="sr-only">ThromBio: Leading Innovations in Stroke Treatment</h1>
                Innovators in Stroke Therapy
              </FadeInSection>
            </div>
            <FadeInSection className="flex max-md:justify-end xl:justify-end min-w-0 delay-[1500ms]" direction="right" alwaysAnimate>
              {/* CSS crop: the GIF is 646px wide but the brain only spans x 85–560 (all frames).
                  Show x 75–571 (496px): image is 646/496 = 130.24% of the box, shifted left 75/496 = 15.12%.
                  Box is 504px = the old 656px display width × 496/646. */}
              <div className="overflow-hidden w-[504px] max-w-full">
                <img src="/Thrombio.gif" alt="Animated visualization of brain and stroke related medical imagery" width={656} className="max-w-none w-[130.24%] -ml-[15.12%] h-auto" />
              </div>
            </FadeInSection>
          </div>

          {showOverlay && <HomeOverlay onClose={handleCloseOverlay} />}

          <div className="pageContainer flex flex-col gap-24 pt-60 md:pt-0 pb-[19rem] md:pb-[16rem]">
            <FadeInSection className="grid md:grid-cols-2 justify-evenly items-center gap-12 xl:gap-28 md:max-xl:pr-[2.7rem] xl:pl-[4.4rem]">
              <div className="flex flex-col gap-[2.8rem] max-w-prose">
                <Link
                  to="/about"
                  onClick={() => trackEvent('cta_click', { cta_name: 'about', cta_location: 'home' })}
                  className="w-fit homepageHeading max-md:mx-auto"
                >
                  About
                  <span className="sr-only">ThromBio: Pioneers in Stroke Therapy</span>
                </Link>
                <p className="homepageText">
                  ThromBio is a drug discovery company whose mission is to advance the global standard of care for the
                  treatment of stroke using novel anti-thrombotic drugs with unprecedented safety and efficacy
                </p>
              </div>
              <div className="rounded-full aspect-square flex justify-center items-center md:p-[12px] md:w-fit md:bg-gradient-to-br from-black to-secondary-bg from-60%">
                <LazyImage
                  src={aboutIcon}
                  alt="Two SEM imaging of resting platelets for stroke research"
                  className="rounded-full object-contain bg-black aspect-square max-md:hidden"
                  width={400}
                  height={400}
                  eager={true}
                />
                <LazyImage
                  src={aboutIconMobile}
                  alt="Two SEM imaging of resting platelets for stroke research"
                  className="h-96 md:object-contain bg-black md:hidden"
                  width={300}
                  height={384}
                  eager={true}
                />
              </div>
            </FadeInSection>
          </div>
          <div className="pageContainer flex flex-col gap-24 pt-0 pb-[10rem] md:pb-[16rem]">
            <FadeInSection className="grid md:grid-cols-2 justify-evenly items-center xl:gap-28 md:max-xl:pl-[2.7rem] xl:pr-[4.4rem]">
              <div className="flex flex-col gap-[2.8rem] max-w-prose">
                <Link
                  to="/leadership/board-of-directors"
                  onClick={() => trackEvent('cta_click', { cta_name: 'leadership', cta_location: 'home' })}
                  className="homepageHeading w-fit max-md:mx-auto"
                >
                  Leadership
                  <span className="sr-only">Leading Experts in Stroke Therapy</span>
                </Link>
                <p className="homepageText">
                  ThromBio programme is driven by international leaders in drug discovery & development, experimental
                  biomedicine, global clinical trials capability and commercialisation
                </p>
              </div>
              <div className="rounded-full aspect-square flex justify-center items-center md:p-[12px] md:w-fit md:bg-gradient-to-b from-black to-secondary-bg from-60%">
                <LazyImage
                  src={leadershipIcon}
                  alt="SEM imaging of an activated platelet for stroke research"
                  className="rounded-full object-contain bg-black aspect-square max-md:hidden"
                  width={400}
                  height={400}
                  eager={true}
                />
                <LazyImage
                  src={leadershipIconMobile}
                  alt="SEM imaging of an activated platelet for stroke research"
                  className="h-96 md:object-contain bg-black md:hidden"
                  width={300}
                  height={384}
                  eager={true}
                />
              </div>
            </FadeInSection>
          </div>
          <div className="flex flex-col items-center px-12 md:px-8 bg-no-repeat bg-left homeTechnologyBg">
            <LazyImage
              src={olcdots}
              alt="Decorative dot pattern"
              width={37}
              height={23}
              className="md:hidden mb-[11.2rem]"
              eager={false}
            />

            <div className="grid xl:grid-cols-2 w-full">
              <FadeInSection className="flex flex-col gap-16 xl:gap-10 md:mb-24">
                <div className="font-syne md:text-primary-blue font-bold text-[3.4rem] md:text-[4rem] xl:text-[4.2rem] leading-[3.6rem] tracking-[5%] md:tracking-[.2rem] text-center max-xl:mb-10">
                  Technology
                  <span className="sr-only">Cutting-Edge Anti-Thrombotic Solutions</span>
                </div>
                <div className="rounded-full aspect-square flex justify-center items-center md:p-[12px] md:w-fit md:bg-gradient-to-br from-black to-secondary-bg from-60% max-xl:hidden">
                  <LazyImage
                    src={technologyIcon}
                    alt="SEM imaging of a group activated platelets for stroke research"
                    className="rounded-full object-contain bg-black aspect-square"
                    width={500}
                    height={500}
                    eager={false}
                  />
                </div>
              </FadeInSection>
              <div className="homeTechnologyContentBg flex flex-col md:items-end max-md:gap-[21rem] gap-16 w-full">
                <FadeInSection className="flex flex-col gap-8 md:items-end delay-500">
                  <div className="flex items-start gap-8 xl:gap-16">
                    <LazyImage
                      src={olcdots}
                      alt="Decorative dot pattern"
                      width={37}
                      height={23}
                      className="max-xl:hidden shrink mt-3"
                      eager={false}
                    />
                    <Link
                      to="/technology/candidate"
                      onClick={() => trackEvent('cta_click', { cta_name: 'tbo-309', cta_location: 'home' })}
                      className="homepageHeading w-fit"
                    >
                      <div className="md:text-right font-syne text-[#C3EEFD] font-bold text-[2.6rem] md:text-[3.3rem] leading-[3.2rem] tracking-[1%] grow text-nowrap">
                        Our Lead Candidate: <br />
                        <span>TBO-309</span>
                        <span className="sr-only">Breakthrough Anti-Thrombotic Drugs for Stroke</span>
                      </div>
                    </Link>
                  </div>
                  <p className="text-[1.9rem] md:text-[2rem] md:leading-[3rem] md:max-xl:max-w-[250px] md:text-right">
                    TBO-309 represents an Australian drug development success story, that has resulted in a novel
                    antiplatelet compound with truly remarkable properties
                  </p>
                </FadeInSection>
                <FadeInSection className="flex flex-col gap-8 xl:pr-32 delay-1000">
                  <div className="flex items-start justify-end gap-8 xl:gap-16">
                    <LazyImage
                      src={olcdots}
                      alt="Decorative dot pattern"
                      width={37}
                      height={23}
                      className="max-xl:hidden shrink mt-3"
                      eager={false}
                    />
                    <Link
                      to="/technology/leadIndication"
                      onClick={() => trackEvent('cta_click', { cta_name: 'lead-indications', cta_location: 'home' })}
                      className="homepageHeading w-fit"
                    >
                      <div className="font-syne text-[#C3EEFD] font-bold text-[2.6rem] md:text-[3.3rem] leading-[3.2rem] tracking-[1%] text-right">
                        Lead Indications
                        <span className="sr-only">TBO-309 for Advanced Stroke Treatment</span>
                      </div>
                    </Link>
                  </div>

                  <p className="text-[1.9rem] md:text-[2rem] md:leading-[3rem] tracking-[2%] md:max-w-[400px] md:text-right ">
                    Acute Ischaemic stroke remains the second largest cause of death and disability globally, with 85%
                    of all strokes caused by blood clots that block blood flow to the brain <br />
                    TBO-309 is currently being trialled in patients in Phase II clinical trials, as an adjunct to
                    thrombolysis (STARS)
                  </p>
                </FadeInSection>
              </div>
            </div>
            <LazyImage
              src={olcdots}
              alt="Decorative dot pattern"
              width={37}
              height={23}
              className="md:hidden mb-[11.2rem] mt-[11.2rem]"
              eager={false}
            />
          </div>


          <FadeInSection className="font-syne md:text-primary-blue font-bold text-[3.4rem] md:text-[4rem] xl:text-[4.2rem] leading-[3.6rem] tracking-[5%] md:tracking-[.2rem] text-center max-xl:mb-10 md:pt-32">
            Clinical Trials
            <span className="sr-only">Cutting-Edge Anti-Thrombotic Solutions</span>
          </FadeInSection>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 w-full">
            <FadeInSection className="flex flex-col gap-16 xl:gap-10 max-md:mb-12 xl:order-2">

              <div className="rounded-full aspect-square flex justify-center items-center md:p-[12px] md:w-fit md:bg-gradient-to-br from-black to-secondary-bg from-60% max-md:hidden">
                <LazyImage
                  src={trialsIcon}
                  alt="SEM imaging of a group activated platelets for stroke research"
                  className="rounded-full object-contain bg-black aspect-square"
                  width={500}
                  height={500}
                  eager={false}
                />
              </div>
            </FadeInSection>
            <div className="flex flex-col md:items-end max-md:gap-4 gap-16 w-full xl:contents">
              <FadeInSection className="flex flex-col gap-8 md:items-end pr-90 xl:order-1 xl:items-center xl:pr-0 delay-500">
                <div className="flex justify-center gap-8 xl:gap-16 max-md:pt-0 pt-10">
                  <Link
                    to="/trials/stars"
                    onClick={() => trackEvent('cta_click', { cta_name: 'stars', cta_location: 'home' })}
                    className="homepageHeading w-fit"
                  >
                    <div className="text-center md:text-right xl:text-center font-syne text-[#B00000] font-bold text-[3.4rem] md:text-[4rem] leading-[3.2rem] tracking-[1%] grow text-nowrap">
                      STARS
                      <span className="sr-only">Breakthrough Anti-Thrombotic Drugs for Stroke</span>
                    </div>
                  </Link>
                </div>
                <p className="text-[1.9rem] md:text-[2rem] md:leading-[3rem] md:max-xl:max-w-[400px] text-center md:text-right xl:text-center px-8">
                  The <span className="font-bold">Safety</span>, <span className="font-bold">Tolerability</span> and Efficacy of <span className="font-bold">Adjunctive</span> TBO-309 in <span className="font-bold">Reperfusion</span> for <span className="font-bold">Stroke</span> in patients with Acute Ischaemic Stroke (AIS)                  </p>
              </FadeInSection>
              <FadeInSection className="rounded-full aspect-square flex justify-center items-center md:hidden">
                <LazyImage
                  src={trialsIconMobile}
                  alt="SEM imaging of an activated platelet for stroke research"
                  className="h-500 md:object-contain md:hidden"
                  width={500}
                  height={500}
                  eager={true}
                />
              </FadeInSection>
              <FadeInSection className="flex flex-col gap-8 xl:pr-32 xl:order-3 xl:items-center delay-1000">
                <div className="flex max-md:justify-center md:justify-end xl:justify-center gap-8 xl:gap-16 max-md:pt-0 pt-10">
                  <Link
                    to="/trials/costars"
                    onClick={() => trackEvent('cta_click', { cta_name: 'costars', cta_location: 'home' })}
                    className="homepageHeading w-fit"
                  >
                    <div className="text-center md:text-right xl:text-center font-syne text-[#B00000] font-bold text-[3.4rem] md:text-[4rem] leading-[3.2rem] tracking-[1%]">
                      CoSTARS
                      <span className="sr-only">TBO-309 for Advanced Stroke Treatment</span>
                    </div>
                  </Link>
                </div>
                <p className="text-[1.9rem] md:text-[2rem] md:leading-[3rem] md:max-xl:max-w-[400px] text-center md:text-right xl:text-center px-8">
                  The <span className="font-bold">Safety</span>, <span className="font-bold">Tolerability</span> and Efficacy of <span className="font-bold">Adjunctive</span> TBO-309 in <span className="font-bold">Reperfusion</span> for <span className="font-bold">Stroke</span> With Tandem Occlusion
                </p>
              </FadeInSection>
            </div>
          </div>

          <div className="flex flex-col justify-center items-center">
            <LazyImage
              src={olcdots}
              alt="Decorative dot pattern"
              width={37}
              height={23}
              className="md:hidden mt-[11.2rem]"
              eager={false}
            />
          </div>

          <div className="max-md:mt-[130px] md:mt-[80px] md:gap-12 flex flex-col justify-center items-center mx-auto">
            <FadeInSection className="md:hidden delay-500">
              <a
                href="/publications"
                onClick={() => trackEvent('cta_click', { cta_name: 'publications', cta_location: 'home', cta_variant: 'mobile' })}
                className="text-[#C3EEFD] text-[2.9rem] font-bold leading-[3.6rem] font-syne text-center w-fit"
              >
                Publications
                <span className="sr-only">ThromBio Publications: Leading Research in Stroke Therapy</span>
              </a>
            </FadeInSection>

            <FadeInSection className="max-md:hidden delay-500">
              <div className="flex flex-col items-center gap-3">
                <a
                  href="/publications"
                  onClick={() => trackEvent('cta_click', { cta_name: 'publications', cta_location: 'home', cta_variant: 'desktop' })}
                  className="text-primary-blue text-[4.8rem] font-bold leading-[3.6rem] font-syne w-fit text-center"
                >
                  Publications
                  <span className="sr-only">ThromBio Publications: Leading Research in Stroke Therapy</span>
                </a>
              </div>
            </FadeInSection>

            <FadeInSection className="rounded-full aspect-square flex justify-center items-center md:p-[12px] md:w-fit md:bg-gradient-to-bl from-black to-secondary-bg from-60%">
              <LazyImage
                src={publicationMediaIconMobile}
                alt="Initial adhesion of a platelet captured by scanning electron microscopy (SEM) for stroke research"
                className="rounded-full max-h-[500px] md:hidden"
                width={400}
                height={400}
                eager={false}
              />
              <LazyImage
                src={publicationMediaIcon}
                alt="Initial adhesion of a platelet captured by scanning electron microscopy (SEM) for stroke research"
                className="rounded-full object-contain bg-black aspect-square max-h-[380px] max-md:hidden"
                width={380}
                height={380}
                eager={false}
              />
            </FadeInSection>

            <FadeInSection className="max-md:hidden delay-500">
              <div className="flex flex-col items-center gap-3">
                <a
                  href="/media"
                  onClick={() => trackEvent('cta_click', { cta_name: 'media', cta_location: 'home', cta_variant: 'desktop' })}
                  className="text-primary-blue text-[4.8rem] font-bold leading-[3.6rem] font-syne text-center"
                >
                  Media
                  <span className="sr-only">ThromBio Media: Latest News and Press Releases on Stroke Therapy</span>
                </a>
              </div>
            </FadeInSection>

            <FadeInSection className="md:hidden delay-500">
              <a
                href="/media"
                onClick={() => trackEvent('cta_click', { cta_name: 'media', cta_location: 'home', cta_variant: 'mobile' })}
                className="text-[#C3EEFD] text-[2.9rem] font-bold leading-[3.6rem] font-syne text-center"
              >
                Media
                <span className="sr-only">ThromBio Media: Latest News and Press Releases on Stroke Therapy</span>
              </a>
            </FadeInSection>
          </div>
          <div className="w-full">
            <LazyImage
              src={mobileCurveFooter}
              alt="Curve-pattern background graphic"
              className="md:hidden -mt-40 object-cover max-w-none w-full"
              width={375}
              height={200}
              eager={false}
            />
          </div>
        </div>
      </div>
    </>
  );
}